import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  TextField,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import ReplayIcon from '@mui/icons-material/Replay';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useAuthStore } from '@/stores/useAuthStore.js';
import { useSimulationTestStore } from '@/stores/useSimulationTestStore.js';
import { useSimulationRunner } from '@/hooks/useSimulationRunner.js';
import { useSimulationExecutionStore } from '@/stores/useSimulationExecutionStore.js';
import { useTestSuiteStore } from '@/stores/useTestSuiteStore.js';
import { useSessionStore } from '@/stores/useSessionStore.js';
import { useWorkbenchContextStore } from '@/stores/useWorkbenchContextStore.js';
import { useDeepLinkContext } from '@/hooks/useDeepLinkContext.js';
import { useRepositories } from '@/infrastructure/repositories/useRepositories.js';
import { createMessageDictionaryService } from '@/domain/message/MessageDictionaryService.js';
import { ReplayDialog } from '@/components/simulation/ReplayDialog.js';
import { buildSelfValidationSuite, buildSelfValidationCases } from '@/domain/self-validation/SelfValidationSuite.js';
import { WorkbenchHeader } from '@/components/workbench/WorkbenchHeader.js';
import { MessageStreamPanel } from '@/components/workbench/MessageStreamPanel.js';
import { InspectorPanel } from '@/components/workbench/InspectorPanel.js';
import { EvidencePanel } from '@/components/workbench/EvidencePanel.js';
import type {
  MessageRule,
  CoordinateConfig,
  MessageDefinition,
  MessageEvent,
  SimulationRun,
  SessionEvidence,
  TestCase,
  TestCaseVerdict,
  TestVerdictStatus,
} from '@k-ucap/shared';

const dictionaryService = createMessageDictionaryService();

const generationModes = [
  { value: 'SINGLE', label: 'Single' },
  { value: 'PERIODIC', label: 'Periodic' },
  { value: 'BULK', label: 'Bulk' },
  { value: 'RANDOM', label: 'Random' },
];

const coordinateModes = [
  { value: 'CENTER_RADIUS', label: 'Center Radius' },
  { value: 'BOUNDING_BOX', label: 'Bounding Box' },
  { value: 'FIXED', label: 'Fixed' },
];

export const ScenarioPage = () => {
  const selectedProject = useAuthStore((s) => s.selectedProject);
  const userProfile = useAuthStore((s) => s.userProfile);
  const environment = selectedProject?.environment ?? 'development';
  const projectId = selectedProject?.id ?? 'demo-project';

  const tests = useSimulationTestStore((s) => s.tests);
  const selectedTestId = useSimulationTestStore((s) => s.selectedTestId);
  const isLoading = useSimulationTestStore((s) => s.isLoading);
  const error = useSimulationTestStore((s) => s.error);
  const loadTests = useSimulationTestStore((s) => s.loadTests);
  const selectTest = useSimulationTestStore((s) => s.selectTest);
  const createTest = useSimulationTestStore((s) => s.createTest);
  const duplicateTest = useSimulationTestStore((s) => s.duplicateTest);
  const deleteTest = useSimulationTestStore((s) => s.deleteTest);
  const updateTest = useSimulationTestStore((s) => s.updateTest);
  const saveCurrentTest = useSimulationTestStore((s) => s.saveCurrentTest);
  const addMessageRule = useSimulationTestStore((s) => s.addMessageRule);
  const updateMessageRule = useSimulationTestStore((s) => s.updateMessageRule);
  const removeMessageRule = useSimulationTestStore((s) => s.removeMessageRule);
  const updateLocationConfig = useSimulationTestStore((s) => s.updateLocationConfig);

  const { simulationRunRepository, messageEventRepository, sessionEvidenceRepository } = useRepositories();
  const { startRun, pauseRun, resumeRun, stopRun, runTestCase, isRunning, isPaused } = useSimulationRunner();
  const elapsedMs = useSimulationExecutionStore((s) => s.elapsedMs);
  const ruleStates = useSimulationExecutionStore((s) => s.ruleStates);
  const currentSession = useSessionStore((s) => s.currentSession);
  const selectedMessageId = useWorkbenchContextStore((s) => s.selectedMessageId);
  const selectMessage = useWorkbenchContextStore((s) => s.selectMessage);
  const selectSession = useWorkbenchContextStore((s) => s.selectSession);
  const deepLink = useDeepLinkContext();

  const [selectedMessageType, setSelectedMessageType] = useState<number | ''>('');
  const [activeTab, setActiveTab] = useState(0);
  const [runs, setRuns] = useState<SimulationRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<SimulationRun | null>(null);
  const [replayOpen, setReplayOpen] = useState(false);
  const [runsLoading, setRunsLoading] = useState(false);
  const [selfValidationOpen, setSelfValidationOpen] = useState(false);
  const [selfValidationRunning, setSelfValidationRunning] = useState(false);
  const [selfValidationResults, setSelfValidationResults] = useState<
    { caseId: string; name: string; status: TestVerdictStatus }[]
  >([]);
  const [streamEvents, setStreamEvents] = useState<MessageEvent[]>([]);
  const [sessionEvidence, setSessionEvidence] = useState<SessionEvidence[]>([]);
  const [selectedStreamEvent, setSelectedStreamEvent] = useState<MessageEvent | null>(null);

  const selectedTest = useMemo(() => tests.find((t) => t.id === selectedTestId) ?? null, [tests, selectedTestId]);

  const messages = useMemo(() => dictionaryService.getAllMessages(), []);

  useEffect(() => {
    loadTests(projectId);
  }, [projectId, loadTests]);

  const loadRuns = useCallback(async () => {
    if (!selectedProject) return;
    setRunsLoading(true);
    try {
      const loaded = await simulationRunRepository.findByProjectId(selectedProject.id);
      setRuns(loaded);
    } catch (err) {
      console.error('Failed to load runs:', err);
    } finally {
      setRunsLoading(false);
    }
  }, [selectedProject, simulationRunRepository]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  // Prompt 14 Phase 3: Message stream subscription
  useEffect(() => {
    if (!selectedProject) {
      setStreamEvents([]);
      return;
    }
    const unsubscribe = messageEventRepository.subscribeToProject?.(selectedProject.id, (events) => {
      setStreamEvents(events);
    });
    if (unsubscribe) return unsubscribe;
    messageEventRepository
      .findByProjectId(selectedProject.id)
      .then((events) => setStreamEvents(events))
      .catch(() => setStreamEvents([]));
    return undefined;
  }, [messageEventRepository, selectedProject]);

  // Prompt 14 Phase 3: Session evidence loading
  useEffect(() => {
    if (!currentSession) {
      setSessionEvidence([]);
      return;
    }
    selectSession(currentSession.id);
    sessionEvidenceRepository
      .findBySessionId(currentSession.id)
      .then((items) => setSessionEvidence(items))
      .catch(() => setSessionEvidence([]));
  }, [currentSession, sessionEvidenceRepository, selectSession]);

  // Prompt 14 Phase 3: deep link sessionId → select run/test
  useEffect(() => {
    if (!deepLink.runId || runs.length === 0) return;
    const matched = runs.find((r) => r.id === deepLink.runId);
    if (matched) setSelectedRun(matched);
  }, [deepLink.runId, runs]);

  const handleCreate = async () => {
    if (!userProfile) return;
    await createTest(projectId, userProfile.uid, environment);
  };

  const handleNameChange = (name: string) => {
    if (!selectedTest) return;
    updateTest({ ...selectedTest, name });
  };

  const handleDescriptionChange = (description: string) => {
    if (!selectedTest) return;
    updateTest({ ...selectedTest, description });
  };

  const handleSave = async () => {
    await saveCurrentTest();
  };

  const handleRunSelfValidation = async () => {
    if (!selectedProject || !userProfile) return;
    setSelfValidationRunning(true);
    try {
      const store = useTestSuiteStore.getState();
      await store.loadSuites(projectId);
      const existing = store.suites.find((s) => s.name === 'K-UCAP Self Validation Suite');
      let suiteId: string;
      if (existing) {
        suiteId = existing.id;
      } else {
        const newSuite = await store.createSuite(projectId, userProfile.uid, environment);
        const builtinSuite = buildSelfValidationSuite(projectId, userProfile.uid);
        await store.updateSuite({ ...builtinSuite, id: newSuite.id, testCaseIds: [] });
        const builtinCases = buildSelfValidationCases(newSuite.id, projectId, userProfile.uid);
        for (const c of builtinCases) {
          const created = await store.createCase(newSuite.id, projectId, userProfile.uid, environment);
          await store.updateCase({
            ...c,
            id: created.id,
            suiteId: newSuite.id,
            createdAt: created.createdAt,
            updatedAt: new Date().toISOString(),
          });
        }
        await store.updateSuite({
          ...builtinSuite,
          id: newSuite.id,
          testCaseIds: builtinCases.map((c) => c.id),
        });
        suiteId = newSuite.id;
      }
      await store.selectSuite(suiteId);
      setActiveTab(2);

      const cases = store.cases.length > 0 ? store.cases : await store.loadCases(suiteId).then(() => store.cases);
      const results: { caseId: string; name: string; status: TestVerdictStatus }[] = [];
      for (const testCase of cases) {
        if (!testCase.enabled) continue;
        const { verdict } = await runTestCase(testCase);
        results.push({ caseId: testCase.id, name: testCase.name, status: verdict.status });
      }
      setSelfValidationResults(results);
      setSelfValidationOpen(true);
    } catch (err) {
      console.error('Self-validation failed:', err);
    } finally {
      setSelfValidationRunning(false);
    }
  };

  const handleAddRule = () => {
    if (!selectedTest || selectedMessageType === '') return;
    const message = dictionaryService.getMessageByType(Number(selectedMessageType));
    addMessageRule(selectedTest.id, Number(selectedMessageType), message?.id);
    setSelectedMessageType('');
  };

  const handleCoordinateChange = (patch: Partial<CoordinateConfig>) => {
    if (!selectedTest) return;
    updateLocationConfig(selectedTest.id, { ...selectedTest.locationConfig.defaultCoordinate, ...patch });
  };

  const totalGenerated = Object.values(ruleStates).reduce((sum, r) => sum + r.generated, 0);
  const totalPass = Object.values(ruleStates).reduce((sum, r) => sum + r.pass, 0);
  const totalWarn = Object.values(ruleStates).reduce((sum, r) => sum + r.warn, 0);
  const totalFail = Object.values(ruleStates).reduce((sum, r) => sum + r.fail, 0);

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" gutterBottom>
        메시지 모의기
      </Typography>

      {!selectedProject && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          프로젝트를 선택해야 시험을 저장하고 실행할 수 있습니다.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => useSimulationTestStore.getState().clearError()}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ flexGrow: 1, minHeight: 0 }}>
        <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Paper sx={{ flexGrow: 1, overflow: 'auto' }}>
            <Box sx={{ p: 1, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button size="small" startIcon={<AddIcon />} onClick={handleCreate} disabled={!selectedProject}>
                새 시험
              </Button>
            </Box>
            <List dense>
              {isLoading ? (
                <ListItem>
                  <ListItemText primary="로딩 중..." />
                </ListItem>
              ) : (
                tests.map((t) => (
                  <ListItem
                    key={t.id}
                    disablePadding
                    secondaryAction={
                      <Box>
                        <IconButton size="small" onClick={() => duplicateTest(t.id)}>
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => deleteTest(t.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemButton selected={selectedTestId === t.id} onClick={() => selectTest(t.id)}>
                      <ListItemText primary={t.name} secondary={t.category} />
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>

        <Grid
          size={{ xs: 12, md: 9 }}
          sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}
        >
          {selectedTest ? (
            <>
              <WorkbenchHeader
                projectName={selectedProject?.name ?? 'No Project'}
                testName={selectedTest.name}
                session={currentSession}
                run={selectedRun}
                elapsedMs={elapsedMs}
                passCount={totalPass}
                warnCount={totalWarn}
                failCount={totalFail}
              />
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">{selectedTest.name}</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        data-testid="scenario-save-button"
                      >
                        Save
                      </Button>
                      {!isRunning ? (
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<PlayArrowIcon />}
                          onClick={() => startRun(selectedTest)}
                          data-testid="scenario-run-button"
                        >
                          Run
                        </Button>
                      ) : isPaused ? (
                        <Button variant="contained" color="primary" startIcon={<PlayArrowIcon />} onClick={resumeRun}>
                          Resume
                        </Button>
                      ) : (
                        <Button variant="contained" color="warning" startIcon={<PauseIcon />} onClick={pauseRun}>
                          Pause
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<StopIcon />}
                        onClick={stopRun}
                        disabled={!isRunning}
                      >
                        Stop
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<AssessmentIcon />}
                        onClick={handleRunSelfValidation}
                        disabled={!selectedProject || selfValidationRunning}
                        data-testid="self-validation-button"
                      >
                        K-UCAP Self Validation Suite
                      </Button>
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="시험 이름"
                        fullWidth
                        size="small"
                        value={selectedTest.name}
                        onChange={(e) => handleNameChange(e.target.value)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="설명"
                        fullWidth
                        size="small"
                        value={selectedTest.description}
                        onChange={(e) => handleDescriptionChange(e.target.value)}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" gutterBottom>
                    위치 설정
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Mode</InputLabel>
                        <Select
                          value={selectedTest.locationConfig.defaultCoordinate.mode}
                          label="Mode"
                          onChange={(e) => handleCoordinateChange({ mode: e.target.value as CoordinateConfig['mode'] })}
                        >
                          {coordinateModes.map((m) => (
                            <MenuItem key={m.value} value={m.value}>
                              {m.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="Center Lat"
                        type="number"
                        fullWidth
                        size="small"
                        value={selectedTest.locationConfig.defaultCoordinate.centerLat ?? ''}
                        onChange={(e) => handleCoordinateChange({ centerLat: Number(e.target.value) })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="Center Lon"
                        type="number"
                        fullWidth
                        size="small"
                        value={selectedTest.locationConfig.defaultCoordinate.centerLon ?? ''}
                        onChange={(e) => handleCoordinateChange({ centerLon: Number(e.target.value) })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="Radius (km)"
                        type="number"
                        fullWidth
                        size="small"
                        value={selectedTest.locationConfig.defaultCoordinate.radiusKm ?? ''}
                        onChange={(e) => handleCoordinateChange({ radiusKm: Number(e.target.value) })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="Min Lat"
                        type="number"
                        fullWidth
                        size="small"
                        value={selectedTest.locationConfig.defaultCoordinate.minLat ?? ''}
                        onChange={(e) => handleCoordinateChange({ minLat: Number(e.target.value) })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="Max Lat"
                        type="number"
                        fullWidth
                        size="small"
                        value={selectedTest.locationConfig.defaultCoordinate.maxLat ?? ''}
                        onChange={(e) => handleCoordinateChange({ maxLat: Number(e.target.value) })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="Min Lon"
                        type="number"
                        fullWidth
                        size="small"
                        value={selectedTest.locationConfig.defaultCoordinate.minLon ?? ''}
                        onChange={(e) => handleCoordinateChange({ minLon: Number(e.target.value) })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="Max Lon"
                        type="number"
                        fullWidth
                        size="small"
                        value={selectedTest.locationConfig.defaultCoordinate.maxLon ?? ''}
                        onChange={(e) => handleCoordinateChange({ maxLon: Number(e.target.value) })}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" gutterBottom>
                    메시지 규칙
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 180 }} data-testid="scenario-message-type-select">
                      <InputLabel>메시지</InputLabel>
                      <Select
                        value={selectedMessageType}
                        label="메시지"
                        onChange={(e) => setSelectedMessageType(Number(e.target.value))}
                      >
                        {messages.map((m) => (
                          <MenuItem key={m.messageType} value={m.messageType}>
                            #{m.messageType} {m.displayName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddRule}
                      disabled={selectedMessageType === ''}
                    >
                      추가
                    </Button>
                  </Box>

                  {selectedTest.messageRules.map((rule) => (
                    <RuleEditor
                      key={rule.id}
                      rule={rule}
                      messages={messages}
                      state={ruleStates[rule.id]}
                      onChange={(updates) => updateMessageRule(selectedTest.id, rule.id, updates)}
                      onDelete={() => removeMessageRule(selectedTest.id, rule.id)}
                    />
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                    <Tab label="실행 통계" />
                    <Tab label="실행 이력" />
                    <Tab label="시험 그룹" />
                    <Tab label="메시지 스트림" />
                    <Tab label="Evidence" />
                  </Tabs>
                  {activeTab === 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        실행 통계
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip label={`Elapsed: ${(elapsedMs / 1000).toFixed(1)}s`} />
                        <Chip label={`Generated: ${totalGenerated}`} />
                        <Chip label={`PASS: ${totalPass}`} color="success" />
                        <Chip label={`WARN: ${totalWarn}`} color="warning" />
                        <Chip label={`FAIL: ${totalFail}`} color="error" />
                      </Box>
                    </Box>
                  )}
                  {activeTab === 1 && (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2">실행 이력</Typography>
                        <Button size="small" startIcon={<RefreshIcon />} onClick={loadRuns} disabled={runsLoading}>
                          새로고침
                        </Button>
                      </Box>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Run Time</TableCell>
                            <TableCell>Test Name</TableCell>
                            <TableCell>Duration</TableCell>
                            <TableCell>Generated</TableCell>
                            <TableCell>PASS</TableCell>
                            <TableCell>WARN</TableCell>
                            <TableCell>FAIL</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {runs.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={9} align="center">
                                <Typography color="text.secondary">실행 이력이 없습니다.</Typography>
                              </TableCell>
                            </TableRow>
                          )}
                          {runs.map((run) => (
                            <TableRow key={run.id}>
                              <TableCell>{new Date(run.startedAt).toLocaleString()}</TableCell>
                              <TableCell>{run.name}</TableCell>
                              <TableCell>{(run.durationMs / 1000).toFixed(1)}s</TableCell>
                              <TableCell>{run.generatedCount}</TableCell>
                              <TableCell>{run.passCount}</TableCell>
                              <TableCell>{run.warnCount}</TableCell>
                              <TableCell>{run.failCount}</TableCell>
                              <TableCell>
                                <Chip
                                  label={run.status}
                                  size="small"
                                  color={
                                    run.status === 'completed'
                                      ? 'success'
                                      : run.status === 'error'
                                        ? 'error'
                                        : 'default'
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  startIcon={<ReplayIcon />}
                                  onClick={() => {
                                    setSelectedRun(run);
                                    setReplayOpen(true);
                                  }}
                                  disabled={run.executionConfig.recordMode === 'NONE'}
                                >
                                  Replay
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                  {activeTab === 2 && <TestSuitePanel />}
                  {activeTab === 3 && (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 8 }}>
                        <MessageStreamPanel
                          events={
                            deepLink.runId ? streamEvents.filter((e) => e.runId === deepLink.runId) : streamEvents
                          }
                          selectedMessageId={selectedMessageId}
                          onSelectMessage={(event) => {
                            setSelectedStreamEvent(event);
                            selectMessage(event.id, event.timestamp);
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <InspectorPanel event={selectedStreamEvent} sessionId={currentSession?.id ?? null} />
                      </Grid>
                    </Grid>
                  )}
                  {activeTab === 4 && (
                    <Box sx={{ height: 400 }}>
                      <EvidencePanel evidence={sessionEvidence} errors={[]} sessionId={currentSession?.id ?? null} />
                    </Box>
                  )}
                </CardContent>
              </Card>
              <ReplayDialog run={selectedRun} open={replayOpen} onClose={() => setReplayOpen(false)} />
              <Dialog open={selfValidationOpen} onClose={() => setSelfValidationOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>K-UCAP Self Validation Results</DialogTitle>
                <DialogContent>
                  {selfValidationResults.length === 0 ? (
                    <Typography color="text.secondary">No cases were run.</Typography>
                  ) : (
                    <List dense>
                      {selfValidationResults.map((result) => (
                        <ListItem key={result.caseId}>
                          <ListItemText
                            primary={result.name}
                            secondary={
                              <Chip
                                label={result.status}
                                size="small"
                                color={
                                  result.status === 'PASS' ? 'success' : result.status === 'FAIL' ? 'error' : 'warning'
                                }
                              />
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setSelfValidationOpen(false)}>Close</Button>
                </DialogActions>
              </Dialog>
            </>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">왼쪽에서 시험을 선택하거나 새 시험을 생성하세요.</Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

const RuleEditor = ({
  rule,
  messages,
  state,
  onChange,
  onDelete,
}: {
  rule: MessageRule;
  messages: MessageDefinition[];
  state: { generated: number; status: string } | undefined;
  onChange: (updates: Partial<MessageRule>) => void;
  onDelete: () => void;
}) => {
  const message = messages.find((m) => m.messageType === rule.messageType);
  return (
    <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">
          #{rule.messageType} {message?.displayName || rule.messageType}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {state && <Chip label={`${state.generated}개`} size="small" />}
          <IconButton size="small" onClick={onDelete}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Mode</InputLabel>
            <Select
              value={rule.generationMode}
              label="Mode"
              onChange={(e) => onChange({ generationMode: e.target.value as MessageRule['generationMode'] })}
            >
              {generationModes.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField
            label="Interval (ms)"
            type="number"
            fullWidth
            size="small"
            value={rule.intervalMs}
            onChange={(e) => onChange({ intervalMs: Number(e.target.value) })}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField
            label="Count"
            type="number"
            fullWidth
            size="small"
            value={rule.count ?? ''}
            onChange={(e) => onChange({ count: e.target.value === '' ? null : Number(e.target.value) })}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField
            label="Delay (ms)"
            type="number"
            fullWidth
            size="small"
            value={rule.startDelayMs}
            onChange={(e) => onChange({ startDelayMs: Number(e.target.value) })}
          />
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
        <FormControlLabel
          control={<Switch checked={rule.enabled} onChange={(e) => onChange({ enabled: e.target.checked })} />}
          label="Enabled"
        />
        <FormControlLabel
          control={
            <Switch checked={rule.randomization} onChange={(e) => onChange({ randomization: e.target.checked })} />
          }
          label="Random"
        />
        <FormControlLabel
          control={
            <Switch checked={rule.faultInjection} onChange={(e) => onChange({ faultInjection: e.target.checked })} />
          }
          label="Fault"
        />
      </Box>
    </Paper>
  );
};

const TestSuitePanel = () => {
  const selectedProject = useAuthStore((s) => s.selectedProject);
  const userProfile = useAuthStore((s) => s.userProfile);
  const projectId = selectedProject?.id ?? 'demo-project';
  const environment = selectedProject?.environment ?? 'development';

  const suites = useTestSuiteStore((s) => s.suites);
  const cases = useTestSuiteStore((s) => s.cases);
  const selectedSuiteId = useTestSuiteStore((s) => s.selectedSuiteId);
  const selectedCaseId = useTestSuiteStore((s) => s.selectedCaseId);
  const isLoading = useTestSuiteStore((s) => s.isLoading);
  const error = useTestSuiteStore((s) => s.error);
  const loadSuites = useTestSuiteStore((s) => s.loadSuites);
  const selectSuite = useTestSuiteStore((s) => s.selectSuite);
  const selectCase = useTestSuiteStore((s) => s.selectCase);
  const createSuite = useTestSuiteStore((s) => s.createSuite);
  const createCase = useTestSuiteStore((s) => s.createCase);
  const updateCase = useTestSuiteStore((s) => s.updateCase);
  const deleteCase = useTestSuiteStore((s) => s.deleteCase);
  const duplicateCase = useTestSuiteStore((s) => s.duplicateCase);
  const clearError = useTestSuiteStore((s) => s.clearError);
  const { runTestCase } = useSimulationRunner();

  const [runningCaseId, setRunningCaseId] = useState<string | null>(null);
  const [lastVerdict, setLastVerdict] = useState<{ caseId: string; verdict: TestCaseVerdict } | null>(null);

  useEffect(() => {
    loadSuites(projectId);
  }, [projectId, loadSuites]);

  const selectedSuite = suites.find((s) => s.id === selectedSuiteId);
  const selectedCase = cases.find((c) => c.id === selectedCaseId);

  const handleCreateSuite = async () => {
    if (!userProfile) return;
    await createSuite(projectId, userProfile.uid, environment);
  };

  const handleCreateCase = async () => {
    if (!selectedSuite || !userProfile) return;
    await createCase(selectedSuite.id, projectId, userProfile.uid, environment);
  };

  const handleRunCase = async (testCase: TestCase) => {
    setRunningCaseId(testCase.id);
    try {
      const { verdict } = await runTestCase(testCase);
      setLastVerdict({ caseId: testCase.id, verdict });
    } catch (err) {
      console.error('runTestCase failed:', err);
    } finally {
      setRunningCaseId(null);
    }
  };

  return (
    <Grid container spacing={2} sx={{ height: '100%' }}>
      <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Paper sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">시험 그룹</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={handleCreateSuite} disabled={!selectedProject}>
              새 그룹
            </Button>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mb: 1 }} onClose={clearError}>
              {error}
            </Alert>
          )}
          <List dense>
            {isLoading ? (
              <ListItem>
                <ListItemText primary="로딩 중..." />
              </ListItem>
            ) : (
              suites.map((suite) => (
                <ListItem key={suite.id} disablePadding>
                  <ListItemButton selected={selectedSuiteId === suite.id} onClick={() => selectSuite(suite.id)}>
                    <ListItemText primary={suite.name} secondary={`Cases: ${suite.testCaseIds.length}`} />
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>
        </Paper>
      </Grid>

      <Grid
        size={{ xs: 12, md: 8 }}
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}
      >
        {selectedSuite ? (
          <>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">{selectedSuite.name}</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={handleCreateCase} disabled={!userProfile}>
                    새 케이스
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selectedSuite.description || '설명 없음'}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <List dense>
                  {cases.length === 0 && (
                    <ListItem>
                      <ListItemText primary="케이스가 없습니다." />
                    </ListItem>
                  )}
                  {cases.map((testCase) => (
                    <ListItem
                      key={testCase.id}
                      disablePadding
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<PlayArrowIcon />}
                            onClick={() => handleRunCase(testCase)}
                            disabled={runningCaseId === testCase.id}
                          >
                            Run
                          </Button>
                          <IconButton size="small" onClick={() => duplicateCase(testCase.id)}>
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => deleteCase(testCase.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemButton selected={selectedCaseId === testCase.id} onClick={() => selectCase(testCase.id)}>
                        <ListItemText primary={testCase.name} secondary={testCase.objective} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>

            {selectedCase && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {selectedCase.name}
                  </Typography>
                  <TextField
                    label="이름"
                    fullWidth
                    size="small"
                    value={selectedCase.name}
                    onChange={(e) => updateCase({ ...selectedCase, name: e.target.value })}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    label="목표"
                    fullWidth
                    size="small"
                    value={selectedCase.objective}
                    onChange={(e) => updateCase({ ...selectedCase, objective: e.target.value })}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    label="설명"
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    value={selectedCase.description}
                    onChange={(e) => updateCase({ ...selectedCase, description: e.target.value })}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="subtitle2" gutterBottom>
                    Pass Criteria
                  </Typography>
                  {selectedCase.passCriteria.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      정의된 기준이 없습니다.
                    </Typography>
                  )}
                  {selectedCase.passCriteria.map((criterion, index) => (
                    <Chip key={index} label={`${criterion.type}`} size="small" sx={{ mr: 1 }} />
                  ))}
                  {lastVerdict?.caseId === selectedCase.id && (
                    <Box sx={{ mt: 2 }}>
                      <Alert
                        severity={
                          lastVerdict.verdict.status === 'PASS'
                            ? 'success'
                            : lastVerdict.verdict.status === 'FAIL'
                              ? 'error'
                              : 'warning'
                        }
                      >
                        {lastVerdict.verdict.status} — Generated: {lastVerdict.verdict.generatedCount}, PASS:{' '}
                        {lastVerdict.verdict.passCount}, WARN: {lastVerdict.verdict.warnCount}, FAIL:{' '}
                        {lastVerdict.verdict.failCount}
                      </Alert>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">왼쪽에서 시험 그룹을 선택하거나 새 그룹을 생성하세요.</Typography>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
};

export default ScenarioPage;
